import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip } from '@/components/ui/status-chip';
import { AreaChip } from '@/components/ui/area-chip';
import { Callout, DetailHead, PartLabel, RecordRow } from '@/components/ui/workspace';
import { useInterviewQuery, type InterviewDetail } from '@/lib/api/discovery-sessions';
import { PersonAvatar } from '../coverage-bits';
import { plural } from '../discovery-logic';
import { AfterSession } from '../transcript-review/transcript-step';
import { BeforeSession, WhatToAsk } from './before-session';
import { formatWhen, longDay, momentChip, shortDay } from './sessions-logic';

/**
 * One interview, split by moment (design note section 3). The head leads
 * with the person ("Maria Lopez", then "Payroll manager, Finance · S6 ·
 * held 14 Oct"), then the areas it covers. Before the session: who and
 * when, what to ask, after the session. After it: who was in the room, then
 * what was said; the plan folds into one record line that opens read-only.
 */
export function InterviewDetailView({
  engagementId,
  interviewId,
  canExtend,
  onOpenPerson,
  onOpenArea,
  interviewName,
}: {
  engagementId: string;
  interviewId: string;
  canExtend: boolean;
  onOpenPerson: (personId: string) => void;
  onOpenArea: (areaId: string) => void;
  interviewName?: (iv: { id: string; code: string; title: string }) => string;
}) {
  const q = useInterviewQuery(engagementId, interviewId);
  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading the interview…
      </div>
    );
  }
  const d = q.data;
  const i = d.interview;
  const held = i.status === 'held';
  const cancelled = i.status === 'cancelled';
  const chip = momentChip(i.state, i.scheduled_at, d.people.length > 0);

  return (
    <div>
      <DetailHead
        leading={<Lead d={d} />}
        title={headTitle(d)}
        meta={headMeta(d)}
        trailing={<StatusChip tone={chip.tone}>{chip.label}</StatusChip>}
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <PartLabel className="mb-0">Covers</PartLabel>
        {d.areas.map((a) => (
          <AreaChip key={a.id} title="Open in Findings" onClick={() => onOpenArea(a.id)}>
            {a.label}
          </AreaChip>
        ))}
      </div>
      {cancelled && i.cancelled ? (
        <>
          <Callout tone="note" className="mb-[18px]">
            <div className="font-semibold text-ink-900">
              Cancelled{i.cancelled.by_name ? ` by ${i.cancelled.by_name}` : ''} on{' '}
              {longDay(i.cancelled.at)}
            </div>
            <div>Reason: {i.cancelled.reason}</div>
            <div className="mt-1 text-meta text-ink-500">
              {i.code} keeps its code and stays on record; the code is never given to another
              interview. Evidence and answers it already has stay as they are.
            </div>
          </Callout>
          <PlanRecord d={d} />
        </>
      ) : held ? (
        <>
          <AfterSession
            engagementId={engagementId}
            d={d}
            canExtend={canExtend}
            interviewName={interviewName}
          />
          <PlanRecord d={d} />
        </>
      ) : (
        <BeforeSession
          engagementId={engagementId}
          d={d}
          canExtend={canExtend}
          onOpenPerson={onOpenPerson}
        />
      )}
    </div>
  );
}

/** The person first: one avatar, a stack for a group, a dashed "?" for no one named yet. */
function Lead({ d }: { d: InterviewDetail }) {
  if (d.people.length === 1) {
    return <PersonAvatar id={d.people[0]!.id} name={d.people[0]!.full_name} size={40} />;
  }
  if (d.people.length > 1) {
    return (
      <span className="inline-flex">
        {d.people.slice(0, 3).map((p, n) => (
          <span key={p.id} className={n > 0 ? '-ml-1.5' : undefined}>
            <PersonAvatar id={p.id} name={p.full_name} size={34} />
          </span>
        ))}
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full border-[1.5px] border-dashed border-ink-300 text-[15px] text-ink-300"
    >
      ?
    </span>
  );
}

function headTitle(d: InterviewDetail): string {
  return d.people.length === 1 ? d.people[0]!.full_name : d.interview.title;
}

/** "Payroll manager, Finance · S6 · held 14 Oct · 60 min" (the code second, rule 20). */
function headMeta(d: InterviewDetail): string {
  const i = d.interview;
  const p0 = d.people.length === 1 ? d.people[0]! : null;
  const who = p0
    ? [p0.title, p0.department].filter(Boolean).join(', ') || i.title
    : d.people.length > 1
      ? d.people.map((p) => p.full_name).join(', ')
      : 'No one named yet';
  const when =
    i.status === 'held'
      ? `held ${shortDay(i.held_at ?? i.scheduled_at)}`
      : i.scheduled_at
        ? formatWhen(i.scheduled_at)
        : null;
  return [who, i.code, when, `${i.duration_minutes} min`].filter(Boolean).join(' · ');
}

/** The plan of a held (or cancelled) interview, folded into one line that opens read-only. */
function PlanRecord({ d }: { d: InterviewDetail }) {
  const [open, setOpen] = React.useState(false);
  const asked = d.questions.filter((q) => q.included).length;
  const invite = d.interview.invite
    ? `, invite sent to ${d.interview.invite.recipients.join(', ')}`
    : '';
  return (
    <div className="mt-4">
      <RecordRow
        muted
        icon={
          open ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          )
        }
        onClick={() => setOpen((v) => !v)}
      >
        <span className="min-w-0 flex-1 truncate">
          Before the session: {plural(asked, 'question')} in {plural(d.areas.length, 'area')}
          {invite}
        </span>
        <span className="text-meta text-ink-500">{open ? 'Hide' : 'Show the plan'}</span>
      </RecordRow>
      {open ? (
        <div className="rounded-card border border-ink-100 bg-paper-0 px-4 py-3.5">
          <WhatToAsk d={d} readOnly />
        </div>
      ) : null}
    </div>
  );
}
